import React from "react";
import styled from "styled-components";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { darkTheme } from "@themes/Theme";
import LifeAtButton, { LifeAtButtonProps } from "../LifeAtButton";

const StyledButton = styled(LifeAtButton)<{
    variant: ButtonVariant;
    shape: ButtonShape;
    btnSize: ButtonSize;
    iconStroke: boolean;
    iconFill: boolean;
    reverse: boolean;
}>`
    justify-content: center;

    height: ${(props) => getHeightForSize(props.btnSize)};
    min-height: ${(props) => getHeightForSize(props.btnSize)};
    max-height: ${(props) => getHeightForSize(props.btnSize)};

    width: fit-content;
    padding: ${(props) => getPaddingForSize(props.btnSize)};
    border: ${(props) => (props.variant ? "none" : "initial")} !important;
    border-radius: ${(props) => getBorderRadiusForShape(props.shape)};

    font-size: ${(props) => getFontSizeForSize(props.btnSize)};

    background-color: ${(props) => getColorForVariant(props.variant)} !important;
    color: ${(props) => (props.variant ? "white" : "initial")} !important;
    * {
        background-color: ${(props) => getColorForVariant(props.variant)} !important;
        color: ${(props) => (props.variant ? "white" : "initial")} !important;
        font-size: ${(props) => getFontSizeForSize(props.btnSize)} !important;
    }

    // NOTE: this is hacked together, based on the styling rules in LifeAtButton.scss
    .lifeat-button-visual {
        flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};
        .lifeat-button-icon {
            padding-left: ${(props) => (props.reverse ? "0.5em" : "inherit")};
        }
        .lifeat-button-text {
            padding-left: ${(props) => (props.reverse ? "unset" : "inherit")};
        }
    }

    svg {
        fill: ${(props) => (props.iconFill ? "white" : null)};
        stroke: ${(props) => (props.iconStroke ? "white" : null)};
    }
`;

type ButtonVariant = "red" | "blue" | "green" | "black";
type ButtonSize = "default" | "small";
type ButtonShape = "default" | "pill";

interface SpecialLifeAtButtonProps extends LifeAtButtonProps {
    /**
     * If the provided icon should have its `fill` property set (to white).
     */
    readonly iconFill?: boolean;
    /**
     * If the provided icon should have its `stroke` property set (to white).
     */
    readonly iconStroke?: boolean;
    /**
     * If the icon should be on the right side of the button, instead of the left (default).
     */
    readonly iconRight?: boolean;
    /**
     * Shape of the button. Defaults to "default".
     */
    readonly shape?: ButtonShape;
    /**
     * Size of the button. Defaults to "default".
     */
    readonly size?: ButtonSize;
    /**
     * Variant (color) of the button. Defaults to "blue".
     */
    readonly variant?: ButtonVariant;
}
export default function SpecialLifeAtButton(props: SpecialLifeAtButtonProps) {
    return (
        <StyledButton
            variant={props.variant ?? "blue"}
            shape={props.shape ?? "default"}
            btnSize={props.size ?? "default"}
            iconStroke={props.iconStroke ?? false}
            iconFill={props.iconFill ?? true}
            reverse={props.iconRight ?? false}
            {...props}
        />
    );
}

const getColorForVariant = (variant: ButtonVariant) => {
    switch (variant) {
        case "red":
            return Styles.lifeatSpecialRed;
        case "blue":
            return Styles.buttonFillBlue;
        case "green":
            return Styles.buttonFillGreen;
        case "black":
            return darkTheme.backgroundPrimary;
        default:
            return "initial";
    }
};

const getHeightForSize = (size: ButtonSize) => {
    switch (size) {
        case "default":
            return Styles.overlayButtonHeight;
        case "small":
            return "28px";
        default:
            return Styles.overlayButtonHeight;
    }
};

const getFontSizeForSize = (size: ButtonSize) => {
    switch (size) {
        case "default":
            return FontSize.medium;
        case "small":
            return FontSize.normal;
        default:
            return FontSize.medium;
    }
};

const getPaddingForSize = (size: ButtonSize) => {
    switch (size) {
        case "default":
            return "6px 10px"; // same as LifeAtButton
        case "small":
            return "4px 6px";
        default:
            return "inherit";
    }
};

const getBorderRadiusForShape = (shape: ButtonShape) => {
    switch (shape) {
        case "default":
            return Styles.borderRadiusSharp;
        case "pill":
            return "20px";
        default:
            return Styles.borderRadiusSharp;
    }
};
